import {
  configureSearchDrawerProductCard,
  optimizeProductImageUrl,
  resolveBoostPrice,
  setCachedProduct,
  storeSearchModalProducts,
} from '@theme/search-drawer-product-card';

const CONFIG_SELECTOR = '[data-boost-search-card-config]';

/**
 * @param {Element|null} script
 * @returns {object|null}
 */
function parseJsonScript(script) {
  if (!(script instanceof HTMLScriptElement)) return null;

  try {
    return JSON.parse(script.textContent || '');
  } catch {
    return null;
  }
}

/**
 * Applies storefront card config from the search section once.
 * @param {ParentNode} [scope]
 */
function ensureCardConfig(scope = document) {
  const root = scope instanceof Document ? scope : document;
  const configScript =
    (scope instanceof Element && scope.querySelector(CONFIG_SELECTOR)) ||
    root.querySelector(CONFIG_SELECTOR);
  const config = parseJsonScript(configScript) || {};

  configureSearchDrawerProductCard({
    shop: config.shop,
    storefrontAccessToken: '6846ce5f06e3b688200521b8293f1e18',
    moneyFormat: config.moneyFormat,
    currency: config.currency,
    country: config.country,
    icons: config.icons || {},
  });
}

/**
 * Prepares a Boost API product for search-drawer-product-card.
 * Uses the API payload directly — only normalizes image URLs and localized price.
 * @param {object} product
 * @returns {object|null}
 */
function mapBoostProduct(product) {
  if (!product?.handle) return null;

  const imagesInfo = (Array.isArray(product.images_info) ? product.images_info : [])
    .map((image) => {
      const src = optimizeProductImageUrl(image?.src || image?.url || '', 800);
      if (!src) return null;
      return Object.assign({}, image, { src });
    })
    .filter(Boolean);

  const images = {};
  imagesInfo.forEach((image, index) => {
    images[String(index + 1)] = image.src;
  });

  if (!Object.keys(images).length && product.image_url) {
    const src = optimizeProductImageUrl(String(product.image_url), 800);
    if (src) images['1'] = src;
  }

  if (!Object.keys(images).length && product.images && typeof product.images === 'object') {
    Object.keys(product.images).forEach((key) => {
      const src = optimizeProductImageUrl(String(product.images[key] || ''), 800);
      if (src) images[key] = src;
    });
  }

  const priceMin = resolveBoostPrice(product, 'price_min');
  const compareAtMin = resolveBoostPrice(product, 'compare_at_price_min');

  // API product is the card source of truth for initial render.
  return Object.assign({}, product, {
    source: 'boost-api',
    price_min: priceMin,
    compare_at_price_min:
      Number.isFinite(compareAtMin) && compareAtMin > priceMin ? compareAtMin : null,
    images: Object.keys(images).length ? images : product.images || {},
    images_info: imagesInfo.length ? imagesInfo : product.images_info || [],
    variants: Array.isArray(product.variants) ? product.variants : [],
    metafields: Array.isArray(product.metafields) ? product.metafields : [],
    tags: Array.isArray(product.tags) ? product.tags : [],
  });
}

/**
 * Extracts products from a Boost API response (search or recommend).
 * @param {object|null} apiResponse
 * @returns {object[]}
 */
function getProductsFromResponse(apiResponse) {
  if (apiResponse && Array.isArray(apiResponse.products)) return apiResponse.products;
  if (apiResponse?.data && Array.isArray(apiResponse.data.products)) return apiResponse.data.products;
  return [];
}

/**
 * @param {unknown} banners
 * @returns {{ url: string, alt: string, href: string }[]}
 */
function normalizeInGridBanners(banners) {
  if (!Array.isArray(banners)) return [];

  return banners
    .map((banner) => {
      if (!banner) return null;
      if (typeof banner === 'string') {
        const url = optimizeProductImageUrl(banner, 1600);
        return url ? { url, alt: '', href: '' } : null;
      }

      const url = optimizeProductImageUrl(
        banner.url || banner.src || banner.image?.url || banner.image?.src || '',
        1600
      );
      if (!url) return null;

      const rawHref = banner.href || banner.link || banner.url_link || '';
      const href =
        typeof rawHref === 'string'
          ? rawHref.trim()
          : rawHref && typeof rawHref === 'object'
            ? String(rawHref.url || rawHref.href || '').trim()
            : '';

      return {
        url,
        alt: String(banner.alt || banner.altText || banner.image?.altText || ''),
        href,
      };
    })
    .filter(Boolean);
}

/**
 * PLP-only in-grid banner cell.
 * Wraps the image in an anchor when `banner.href` is set (index-mapped from
 * collection metafield custom.ingrid_banner_links).
 * @param {{ url: string, alt?: string, href?: string }} banner
 * @param {'half'|'full'} span
 * @param {number} bannerIndex
 * @returns {HTMLElement}
 */
function createInGridBannerCell(banner, span, bannerIndex) {
  const cell = document.createElement('div');
  cell.className =
    span === 'full'
      ? 'plp-collection-grid__banner plp-collection-grid__banner--full'
      : 'plp-collection-grid__banner plp-collection-grid__banner--half';
  cell.setAttribute('data-plp-in-grid-banner', String(bannerIndex + 1));
  cell.setAttribute('data-plp-banner-span', span);

  const img = document.createElement('img');
  img.className = 'plp-collection-grid__banner-image';
  img.src = banner.url;
  img.alt = banner.alt || '';
  img.loading = 'eager';
  img.decoding = 'async';
  img.setAttribute('sizes', span === 'full' ? '100vw' : '(min-width: 750px) 50vw, 100vw');

  const href = String(banner.href || '').trim();
  if (href) {
    const link = document.createElement('a');
    link.className = 'plp-collection-grid__banner-link';
    link.href = href;
    link.appendChild(img);
    cell.appendChild(link);
  } else {
    cell.appendChild(img);
  }

  return cell;
}

/**
 * Builds a product grid cell with search-drawer-product-card.
 * @param {object} product
 * @param {number} index
 * @param {number} eagerImageCount
 * @returns {{ cell: HTMLElement, card: HTMLElement }}
 */
function createProductCell(product, index, eagerImageCount) {
  const cell = document.createElement('div');
  cell.className = 'boost-collection__product-cell';
  cell.id = `boost-collection-cell-${product.handle}-${index}`;
  cell.setAttribute('data-boost-grid-cell', '');
  cell.setAttribute('data-boost-grid-index', String(index));
  cell.setAttribute('data-boost-product-handle', product.handle);

  const card = document.createElement('search-drawer-product-card');
  card.className = 'search-drawer-product-card';
  if (index < eagerImageCount) {
    card.setAttribute('data-image-priority', 'high');
  }

  if (product.id) setCachedProduct(String(product.id), product);
  card.product = product;
  cell.appendChild(card);

  return { cell, card };
}

/**
 * Appends products, optionally inserting PLP in-grid banners after the 6th and 10th products.
 * Screenshot layout: 6 cards → half banner → 4 cards → full banner.
 * @param {DocumentFragment} fragment
 * @param {object[]} products
 * @param {{ url: string, alt: string, href?: string }[]} banners
 * @param {{ startIndex?: number, eagerImageCount?: number, insertBanners?: boolean }} [options]
 * @returns {{ cards: HTMLElement[], productCount: number }}
 */
function appendProductsWithInGridBanners(fragment, products, banners, options = {}) {
  const startIndex =
    typeof options.startIndex === 'number' && options.startIndex >= 0
      ? Math.floor(options.startIndex)
      : 0;
  const eagerImageCount =
    typeof options.eagerImageCount === 'number' && options.eagerImageCount > 0
      ? Math.floor(options.eagerImageCount)
      : 0;
  const insertBanners = options.insertBanners === true;
  const bannerList = normalizeInGridBanners(banners);
  const cards = [];

  products.forEach((product, index) => {
    const absoluteIndex = startIndex + index;
    const built = createProductCell(product, absoluteIndex, eagerImageCount);
    fragment.appendChild(built.cell);
    cards.push(built.card);

    if (!insertBanners) return;

    // After 6 product cards → banner 1 (2-col on desktop)
    if (absoluteIndex + 1 === 6 && bannerList[0]) {
      fragment.appendChild(createInGridBannerCell(bannerList[0], 'half', 0));
    }

    // After 10 product cards → banner 2 (full-width on desktop)
    if (absoluteIndex + 1 === 10 && bannerList[1]) {
      fragment.appendChild(createInGridBannerCell(bannerList[1], 'full', 1));
    }
  });

  return { cards, productCount: products.length };
}

/**
 * Renders Boost search/collection results with USA Online product cards.
 * Pass `inGridBanners` only from PLP — search page must omit it.
 * @param {HTMLElement} gridContainer
 * @param {object|null} apiResponse
 * @param {{
 *   limit?: number,
 *   emptyText?: string,
 *   eagerImageCount?: number,
 *   inGridBanners?: { url: string, alt?: string, href?: string }[],
 * }} [options]
 * @returns {Promise<void>}
 */
async function renderSearchDrawerProductGrid(gridContainer, apiResponse, options = {}) {
  const maxCards =
    typeof options.limit === 'number' && options.limit > 0 ? Math.floor(options.limit) : 100;
  const emptyText =
    options.emptyText != null && String(options.emptyText) ? String(options.emptyText) : 'No matches';
  const eagerImageCount =
    typeof options.eagerImageCount === 'number' && options.eagerImageCount > 0
      ? Math.floor(options.eagerImageCount)
      : 0;
  const insertBanners = Array.isArray(options.inGridBanners);

  const gridEl = gridContainer?.querySelector?.('.js-grid');
  if (!gridEl) return;

  await customElements.whenDefined('search-drawer-product-card');
  ensureCardConfig(
    gridContainer.closest('.boost-search-page, .plp-collection-grid, .shopify-section') || document
  );

  const products = getProductsFromResponse(apiResponse)
    .map(mapBoostProduct)
    .filter(Boolean)
    .slice(0, maxCards);

  if (!products.length) {
    gridEl.innerHTML = `<div class="js-grid__full-row"><p>${escapeHtml(emptyText)}</p></div>`;
    gridContainer.classList?.remove('is-loading');
    return;
  }

  storeSearchModalProducts(products);

  const fragment = document.createDocumentFragment();
  appendProductsWithInGridBanners(
    fragment,
    products,
    insertBanners ? options.inGridBanners : [],
    {
      startIndex: 0,
      eagerImageCount,
      insertBanners,
    }
  );

  gridEl.replaceChildren(fragment);
  gridContainer.classList?.remove('is-loading');
  // Color swatches: each search-drawer-product-card fetches GraphQL on connect.
}

/**
 * Appends more Boost products into an existing grid (infinite scroll).
 * Never inserts in-grid banners on subsequent pages.
 * @param {HTMLElement} gridContainer
 * @param {object|null} apiResponse
 * @param {{ limit?: number, startIndex?: number }} [options]
 * @returns {Promise<{ appended: number }>}
 */
async function appendSearchDrawerProductGrid(gridContainer, apiResponse, options = {}) {
  const maxCards =
    typeof options.limit === 'number' && options.limit > 0 ? Math.floor(options.limit) : 100;
  const startIndex =
    typeof options.startIndex === 'number' && options.startIndex >= 0
      ? Math.floor(options.startIndex)
      : 0;

  const gridEl = gridContainer?.querySelector?.('.js-grid');
  if (!gridEl) return { appended: 0 };

  await customElements.whenDefined('search-drawer-product-card');
  ensureCardConfig(
    gridContainer.closest('.boost-search-page, .plp-collection-grid, .shopify-section') || document
  );

  const products = getProductsFromResponse(apiResponse)
    .map(mapBoostProduct)
    .filter(Boolean)
    .slice(0, maxCards);

  if (!products.length) return { appended: 0 };

  storeSearchModalProducts(products);

  const fragment = document.createDocumentFragment();
  const { productCount } = appendProductsWithInGridBanners(fragment, products, [], {
    startIndex,
    eagerImageCount: 0,
    insertBanners: false,
  });

  gridEl.appendChild(fragment);
  gridContainer.classList?.remove('is-loading');
  // Color swatches: each search-drawer-product-card fetches GraphQL on connect.

  return { appended: productCount };
}

/**
 * @param {string} value
 * @returns {string}
 */
function escapeHtml(value) {
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/**
 * Registers API → search-drawer-product-card grid renderers on window.theme.
 * @returns {boolean}
 */
function patchBoostFilterGridRenderer() {
  window.theme = window.theme || {};
  window.theme.renderSearchDrawerProductGrid = renderSearchDrawerProductGrid;
  window.theme.appendSearchDrawerProductGrid = appendSearchDrawerProductGrid;

  const boostFilter = window.theme.boostFilter;
  if (!boostFilter) return false;
  if (boostFilter.__searchDrawerGridPatched) return true;

  boostFilter.renderProductGrid = renderSearchDrawerProductGrid;
  boostFilter.renderSearchDrawerProductGrid = renderSearchDrawerProductGrid;
  boostFilter.appendSearchDrawerProductGrid = appendSearchDrawerProductGrid;
  boostFilter.__searchDrawerGridPatched = true;
  return true;
}

/**
 * Hydrates Liquid-rendered search cards (empty-search collection / product_list sources).
 * @param {ParentNode} [scope]
 * @returns {Promise<void>}
 */
async function hydrateLiquidSearchCards(scope = document) {
  const cards = scope.querySelectorAll('[data-boost-search-product-card]');
  if (!cards.length) return;

  await customElements.whenDefined('search-drawer-product-card');
  ensureCardConfig(scope.querySelector('.boost-search-page') || document);

  cards.forEach((item) => {
    if (!(item instanceof HTMLElement)) return;

    const card = item.querySelector('search-drawer-product-card');
    const payloadScript = item.querySelector('[data-product-payload]');
    if (!(card instanceof HTMLElement)) return;

    const product = parseJsonScript(payloadScript);
    if (!product?.handle) return;

    if (product.id) setCachedProduct(String(product.id), product);
    if ('product' in card) card.product = product;
    // GraphQL swatches run from the card web component on connect / product set.
  });
}

function boot() {
  window.theme = window.theme || {};

  const tryPatch = () => {
    if (patchBoostFilterGridRenderer()) {
      hydrateLiquidSearchCards(document);
      return true;
    }
    return false;
  };

  if (tryPatch()) return;

  document.addEventListener(
    'boost-filter-ready',
    () => {
      tryPatch();
    },
    { once: true }
  );

  let attempts = 0;
  const poll = window.setInterval(() => {
    attempts += 1;
    if (tryPatch() || attempts > 200) window.clearInterval(poll);
  }, 25);
}

boot();

export {
  mapBoostProduct,
  renderSearchDrawerProductGrid,
  appendSearchDrawerProductGrid,
  patchBoostFilterGridRenderer,
};
